import {
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  Box,
} from "@mui/material";
import { useEffect, useState } from "react";
import api, { isCancel } from "../../services/api/axios";
import { useAuth } from "../../providers/AuthProvider";

function CompanyAutocomplete({
  onCompanySelect,
  selectedCompany, // { id, name, source } | null
  source = "all", // "bre" | "prospecting" | "all"
  label = "Company",
  sx = {}, // Wrapper styling
  textFieldSx = {}, // Inner TextField styling (🔥 NEW)
}) {
  const { token } = useAuth();

  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------
  // Fetch companies (BRE + Prospecting)
  // ---------------------------------------------
  useEffect(() => {
    if (!token) return;

    const controller = new AbortController();
    setLoading(true);

    const requests = [];

    if (source === "bre" || source === "all") {
      requests.push(
        api.get("/user-companies/all", {
          signal: controller.signal,
        }),
      );
    }

    if (source === "prospecting" || source === "all") {
      requests.push(
        api.get("/prospecting-companies", {
          params: { limit: 500 },
          signal: controller.signal,
        }),
      );
    }

    Promise.all(requests)
      .then((responses) => {
        const merged = [];

        responses.forEach((res) => {
          const rows = Array.isArray(res.data)
            ? res.data
            : res.data?.data || [];

          const isProspect = res.config.url.includes("prospecting");

          rows.forEach((c) => {
            merged.push({
              id: c.id,
              name: c.name,
              source: isProspect ? "prospecting" : "bre",
            });
          });
        });

        setOptions(merged);
      })
      .catch((err) => {
        if (!isCancel(err)) {
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [token, source]);

  // ---------------------------------------------
  // Sync selected value (EDIT MODE)
  // ---------------------------------------------
  useEffect(() => {
    if (!selectedCompany) {
      setValue(null);
      setInputValue("");
      return;
    }

    // 1️⃣ Try exact match (id + source)
    let match = options.find(
      (o) =>
        String(o.id) === String(selectedCompany.id) &&
        o.source === selectedCompany.source,
    );

    // 2️⃣ Fallback: match by ID only
    if (!match) {
      match = options.find((o) => String(o.id) === String(selectedCompany.id));
    }

    // 3️⃣ Final fallback: inject missing option
    if (!match && selectedCompany.name) {
      match = {
        id: selectedCompany.id,
        name: selectedCompany.name,
        source: selectedCompany.source || "bre",
      };

      setOptions((prev) => [match, ...prev]);
    }

    if (match) {
      setValue(match);
      setInputValue(match.name);
    } else {
      setValue(null);
      setInputValue("");
    }
  }, [options, selectedCompany]);

  // ---------------------------------------------
  // Selection handler
  // ---------------------------------------------
  const handleSelect = (_event, newValue) => {
    setValue(newValue || null);
    setInputValue(newValue?.name || "");

    onCompanySelect?.(
      newValue
        ? {
            id: newValue.id,
            name: newValue.name,
            source: newValue.source,
          }
        : null,
    );
  };

  return (
    <Autocomplete
      options={options}
      value={value}
      inputValue={inputValue}
      loading={loading}
      onChange={handleSelect}
      onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(a, b) =>
        String(a?.id) === String(b?.id) && a?.source === b?.source
      }
      sx={{ width: "100%", ...sx }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.source}-${option.id}`}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{option.name}</span>
            <Chip
              size="small"
              label={option.source === "bre" ? "BRE" : "PROSPECT"}
              color={option.source === "bre" ? "success" : "info"}
            />
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          sx={textFieldSx} // 🔥 Injected styling here
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default CompanyAutocomplete;
